import { useEffect, useState } from 'react';
import { Plus, Code, Edit, Trash2, Lightbulb, AlertCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useHeadScripts } from '@/hooks/useHeadScripts';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';

// Presets de prioridade por tipo de script
const PRIORITY_PRESETS = {
  analytics: 100,
  heatmap: 50,
  conversion: 80,
  other: 30,
};

// Opções predefinidas de prioridade
const PRIORITY_OPTIONS = [
  { value: 100, label: 'Crítica (100)', description: 'Carrega primeiro' },
  { value: 80, label: 'Alta (80)', description: 'Muito importante' },
  { value: 50, label: 'Média (50)', description: 'Importante' },
  { value: 30, label: 'Baixa (30)', description: 'Não essencial' },
  { value: 0, label: 'Mínima (0)', description: 'Carrega por último' },
];

export default function HeadScripts() {
  const { scripts, isLoading, createScript, updateScript, deleteScript, toggleScript } = useHeadScripts();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingScript, setEditingScript] = useState<any>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [customPriority, setCustomPriority] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    script_content: '',
    script_type: 'other' as 'analytics' | 'heatmap' | 'conversion' | 'other',
    priority: 0,
    is_active: true,
  });

  useEffect(() => {
    document.title = 'Dashi | Scripts';
  }, []);

  const handleOpenDialog = (script?: any) => {
    if (script) {
      setEditingScript(script);
      setFormData({
        name: script.name,
        description: script.description || '',
        script_content: script.script_content,
        script_type: script.script_type,
        priority: script.priority,
        is_active: script.is_active,
      });
      // Check if priority is custom
      const isCustom = !PRIORITY_OPTIONS.some(opt => opt.value === script.priority);
      setCustomPriority(isCustom);
    } else {
      setEditingScript(null);
      const defaultPriority = PRIORITY_PRESETS['other'];
      setFormData({
        name: '',
        description: '',
        script_content: '',
        script_type: 'other',
        priority: defaultPriority,
        is_active: true,
      });
      setCustomPriority(false);
    }
    setIsDialogOpen(true);
  };

  // Função para aplicar preset de prioridade ao mudar o tipo
  const handleTypeChange = (type: 'analytics' | 'heatmap' | 'conversion' | 'other') => {
    const suggestedPriority = PRIORITY_PRESETS[type];
    setFormData({ 
      ...formData, 
      script_type: type,
      priority: customPriority ? formData.priority : suggestedPriority 
    });
  };

  // Detectar conflitos de prioridade
  const hasPriorityConflict = scripts?.some(
    s => s.priority === formData.priority && 
    s.id !== editingScript?.id && 
    s.is_active
  );

  // Calcular ordem de carregamento dos scripts ativos
  const getSortedActiveScripts = () => {
    if (!scripts) return [];
    return scripts
      .filter(s => s.is_active)
      .sort((a, b) => {
        if (b.priority !== a.priority) {
          return b.priority - a.priority;
        }
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      });
  };

  const getScriptOrder = (scriptId: string) => {
    const sorted = getSortedActiveScripts();
    return sorted.findIndex(s => s.id === scriptId) + 1;
  };

  const getPriorityConflictCount = (priority: number) => {
    return scripts?.filter(s => s.priority === priority && s.is_active).length || 0;
  };

  const handleSubmit = async () => {
    if (editingScript) {
      await updateScript.mutateAsync({ id: editingScript.id, ...formData });
    } else {
      await createScript.mutateAsync(formData);
    }
    setIsDialogOpen(false);
  };

  const handleToggle = (id: string, current: boolean) => {
    toggleScript.mutate({ id, is_active: !current });
  };

  const handleDelete = async () => {
    if (deleteId) {
      await deleteScript.mutateAsync(deleteId);
      setDeleteId(null);
    }
  };

  const scriptTypeLabels = {
    analytics: 'Analytics',
    heatmap: 'Mapa de Calor',
    conversion: 'Conversão',
    other: 'Outro',
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Code className="h-8 w-8" />
            Scripts
          </h1>
          <p className="text-muted-foreground mt-1">
            Gerencie scripts externos (Hotjar, Google Analytics, Facebook Pixel, etc.)
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Adicionar Código
        </Button>
      </div>

      {/* Instruções */}
      <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-900 dark:text-blue-100">
            <Code className="h-5 w-5" />
            Como usar
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-blue-700 dark:text-blue-300 space-y-2">
          <p>• Adicione códigos de rastreamento completos (incluindo tags <code>&lt;script&gt;</code>)</p>
          <p>• Os scripts ativos serão injetados automaticamente na tag <code>&lt;head&gt;</code></p>
          <p>• Use a prioridade para controlar a ordem de carregamento (maior = carrega primeiro)</p>
          <p>• Você pode ativar/desativar scripts sem deletá-los</p>
        </CardContent>
      </Card>

      {/* Tabela de Scripts */}
      <Card>
        <CardHeader>
          <CardTitle>Scripts Cadastrados</CardTitle>
          <CardDescription>
            {scripts?.length || 0} {scripts?.length === 1 ? 'script cadastrado' : 'scripts cadastrados'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : scripts && scripts.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Prioridade</TableHead>
                  <TableHead>Ordem</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scripts.map((script) => {
                  const conflictCount = getPriorityConflictCount(script.priority);
                  const hasConflict = conflictCount > 1;
                  
                  return (
                    <TableRow key={script.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{script.name}</div>
                          {script.description && (
                            <div className="text-xs text-muted-foreground">{script.description}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{scriptTypeLabels[script.script_type]}</Badge>
                      </TableCell>
                      <TableCell>
                        <TooltipProvider>
                          <div className="flex items-center gap-2">
                            <Badge 
                              variant="secondary"
                              className={cn(
                                script.priority >= 80 && "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200",
                                script.priority >= 50 && script.priority < 80 && "bg-yellow-100 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-200",
                                script.priority < 50 && "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200"
                              )}
                            >
                              {script.priority}
                            </Badge>
                            {hasConflict && (
                              <Tooltip>
                                <TooltipTrigger>
                                  <AlertCircle className="h-4 w-4 text-amber-500" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{conflictCount} scripts com esta prioridade</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell>
                        {script.is_active ? (
                          <Badge variant="outline" className="font-mono">
                            #{getScriptOrder(script.id)}
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={script.is_active}
                            onCheckedChange={() => handleToggle(script.id, script.is_active)}
                          />
                          <span className="text-sm">
                            {script.is_active ? 'Ativo' : 'Inativo'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(script)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteId(script.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <Code className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Nenhum código de rastreamento cadastrado</p>
              <Button onClick={() => handleOpenDialog()} className="mt-4">
                Adicionar Primeiro Código
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de Criação/Edição */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingScript ? 'Editar Código' : 'Adicionar Código de Rastreamento'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome do Código *</Label>
              <Input
                id="name"
                placeholder="Ex: Hotjar, Google Analytics 4"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrição (Opcional)</Label>
              <Input
                id="description"
                placeholder="Ex: Rastreamento de comportamento de usuários"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="script_type">Tipo</Label>
                <Select
                  value={formData.script_type}
                  onValueChange={handleTypeChange}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="analytics">Analytics</SelectItem>
                    <SelectItem value="heatmap">Mapa de Calor</SelectItem>
                    <SelectItem value="conversion">Conversão</SelectItem>
                    <SelectItem value="other">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="priority">Prioridade</Label>
                {customPriority ? (
                  <div className="flex gap-2">
                    <Input
                      id="priority"
                      type="number"
                      min="0"
                      max="100"
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setCustomPriority(false);
                        setFormData({ ...formData, priority: PRIORITY_PRESETS[formData.script_type] });
                      }}
                    >
                      Preset
                    </Button>
                  </div>
                ) : (
                  <Select
                    value={formData.priority.toString()}
                    onValueChange={(value) => {
                      if (value === 'custom') {
                        setCustomPriority(true);
                      } else {
                        setFormData({ ...formData, priority: parseInt(value) });
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITY_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value.toString()}>
                          <div className="flex flex-col">
                            <span>{opt.label}</span>
                            <span className="text-xs text-muted-foreground">{opt.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                      <SelectItem value="custom">
                        <span>✏️ Personalizado...</span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
                {!customPriority && (
                  <p className="text-xs text-muted-foreground">
                    💡 Sugerido para {scriptTypeLabels[formData.script_type]}
                  </p>
                )}
              </div>
            </div>

            {/* Aviso de Conflito de Prioridade */}
            {hasPriorityConflict && (
              <Alert variant="default" className="border-amber-500 bg-amber-50 dark:bg-amber-950">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <AlertTitle className="text-amber-900 dark:text-amber-100">Prioridade Duplicada</AlertTitle>
                <AlertDescription className="text-amber-800 dark:text-amber-200">
                  Já existe outro script ativo com prioridade {formData.priority}.
                  Scripts com mesma prioridade serão carregados por ordem de criação.
                </AlertDescription>
              </Alert>
            )}

            {/* Guia de Recomendações */}
            <Card className="bg-muted/50 border-primary/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 text-primary" />
                  Recomendações de Prioridade
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Google Analytics, Meta Pixel:</span>
                  <Badge variant="outline" className="bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200">80-100</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Hotjar, Microsoft Clarity:</span>
                  <Badge variant="outline" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-200">40-60</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Chat/Suporte (Zendesk, Intercom):</span>
                  <Badge variant="outline" className="bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200">20-40</Badge>
                </div>
                <p className="text-muted-foreground pt-1 italic">
                  Scripts com maior prioridade carregam primeiro e têm melhor precisão de dados.
                </p>
              </CardContent>
            </Card>

            <div className="space-y-2">
              <Label htmlFor="script_content">Código Completo *</Label>
              <Textarea
                id="script_content"
                placeholder="Cole aqui o código fornecido pela ferramenta (incluindo as tags <script>)"
                value={formData.script_content}
                onChange={(e) => setFormData({ ...formData, script_content: e.target.value })}
                rows={12}
                className="font-mono text-xs"
              />
              <p className="text-xs text-muted-foreground">
                💡 Dica: Cole o código exatamente como fornecido pelo serviço (Hotjar, Google Analytics, etc.)
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label htmlFor="is_active">Ativar automaticamente após salvar</Label>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!formData.name || !formData.script_content}
              >
                {editingScript ? 'Salvar Alterações' : 'Adicionar Código'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover este código? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Deletar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
